import type { CSSProperties, ReactNode } from 'react'

export type FeedTone = 'danger' | 'warn' | 'teal' | 'neutral' | 'rose'
export type FeedSeverity = 'critical' | 'high' | 'medium' | 'info'

const ICON_BG: Record<FeedTone, string> = {
  danger: 'var(--danger-soft)',
  warn: 'var(--warm-soft)',
  teal: 'var(--accent-soft)',
  neutral: 'var(--surface-3)',
  rose: 'var(--rose-soft)',
}
const ICON_FG: Record<FeedTone, string> = {
  danger: 'var(--danger)',
  warn: 'var(--warm)',
  teal: 'var(--accent)',
  neutral: 'var(--text-3)',
  rose: 'var(--rose)',
}

// Per-severity card sizing — mirrors the s04 `body.lv-sev .list-item[data-sev=...]`
// rules: critical is emphasised (bigger icon/title, glow), medium/info shrink.
const SEV_SIZING: Record<FeedSeverity, { icon: number; iconSvg: number; title: number; pad: string }> = {
  critical: { icon: 40, iconSvg: 16, title: 15, pad: '18px' },
  high: { icon: 36, iconSvg: 16, title: 14, pad: '12px 16px' },
  medium: { icon: 26, iconSvg: 12, title: 12.5, pad: '9px 14px' },
  info: { icon: 26, iconSvg: 12, title: 12.5, pad: '9px 14px' },
}

interface FeedItemProps {
  /** Small leading glyph (svg/emoji/text); tinted by `tone`. */
  icon?: ReactNode
  tone?: FeedTone
  title: ReactNode
  /** Right-aligned timestamp (mono). */
  time?: ReactNode
  /** Secondary line under the title. */
  sub?: ReactNode
  /** Chips/badges row under the sub-line (compose <Pill variant="chip" />). */
  chips?: ReactNode
  /** Always-visible card footer, e.g. a grouped-update dropdown. */
  footer?: ReactNode
  /** Right-side severity badge or value (row variant only). */
  trailing?: ReactNode
  /** Unread accent pip on the far left. */
  unread?: boolean
  selected?: boolean
  reviewed?: boolean
  onClick?: () => void
  className?: string
  style?: CSSProperties
  /**
   * `row` (default) = the flat s12 Alert Feed divider row.
   * `card` = the s04 Command Center rounded frosted card with severity-driven
   * sizing and an inline expansion slot.
   */
  variant?: 'row' | 'card'
  /** Drives card sizing/emphasis (card variant). */
  severity?: FeedSeverity
  /** Inline detail rendered below the row/card when `selected` (both variants). */
  expansion?: ReactNode
}

/**
 * FeedItem — the alert-feed / notification list item: tinted icon, title + time,
 * sub-line, chips. Two shapes share one component:
 *  - `variant="row"`  — flat divider row (s12 Alert Feed).
 *  - `variant="card"` — rounded frosted card with severity sizing + inline
 *    expansion (s04 Command Center).
 * Aurora+ source: `.alert-row` (s12) and `.list-item` + `.li-inline` (s04).
 */
export function FeedItem({
  icon,
  tone = 'neutral',
  title,
  time,
  sub,
  chips,
  footer,
  trailing,
  unread = false,
  selected = false,
  reviewed = false,
  onClick,
  className,
  style,
  variant = 'row',
  severity = 'high',
  expansion,
}: FeedItemProps) {
  if (variant === 'card') {
    const sz = SEV_SIZING[severity]
    const minor = severity === 'medium' || severity === 'info'
    const emphasised = severity === 'critical' && !reviewed

    return (
      <div
        className={className}
        onClick={onClick}
        data-sev={severity}
        style={{
          position: 'relative',
          background: selected ? 'var(--surface-2)' : 'var(--surface)',
          border: `1px solid ${emphasised ? 'rgba(196,59,78,0.25)' : 'var(--border)'}`,
          borderRadius: 16,
          padding: sz.pad,
          marginBottom: 8,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.32s var(--ease-spring), box-shadow 0.32s var(--ease-spring), border-color 0.32s var(--ease-spring)',
          boxShadow: emphasised
            ? '0 2px 8px rgba(196,59,78,0.08), 0 20px 48px -20px rgba(196,59,78,0.22)'
            : 'none',
          opacity: reviewed ? 0.6 : minor ? 0.88 : 1,
          animation: 'fadeStagger 0.5s var(--ease-spring) both',
          ...style,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {unread && !reviewed ? (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: ICON_FG[tone],
                boxShadow: `0 0 0 3px ${ICON_BG[tone]}`,
                flexShrink: 0,
                marginTop: 5,
              }}
            />
          ) : (
            <span style={{ width: 8, flexShrink: 0 }} />
          )}
          {icon != null && (
            <span
              style={{
                width: sz.icon,
                height: sz.icon,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 1,
                background: ICON_BG[tone],
                color: ICON_FG[tone],
                transition: 'all 0.2s',
              }}
            >
              {icon}
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <div
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: sz.title,
                  fontWeight: reviewed ? 500 : minor ? 600 : 700,
                  color: 'var(--text-1)',
                  lineHeight: 1.3,
                  letterSpacing: '-0.01em',
                }}
              >
                {title}
              </div>
              {time != null && (
                <div
                  className="tnum"
                  style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 500, color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0, marginTop: 1 }}
                >
                  {time}
                </div>
              )}
            </div>
            {sub != null && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: chips ? 8 : 0 }}>
                {sub}
              </div>
            )}
            {chips != null && <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>{chips}</div>}
            {footer != null && <div style={{ marginTop: 10 }}>{footer}</div>}
            {selected && expansion != null && (
              <div style={{ marginTop: 10, paddingTop: 14, borderTop: '1px solid var(--border)' }}>{expansion}</div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── row variant (default, unchanged) ──────────────────────────────────────
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '13px 24px',
        borderBottom: '1px solid var(--border)',
        background: selected ? 'var(--accent-soft)' : 'transparent',
        opacity: reviewed ? 0.55 : 1,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.15s',
        ...style,
      }}
    >
      {selected && (
        <span
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'var(--accent)' }}
        />
      )}
      {unread && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: ICON_FG[tone],
            flexShrink: 0,
            marginTop: 13,
          }}
        />
      )}
      {icon != null && (
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: ICON_BG[tone],
            color: ICON_FG[tone],
          }}
        >
          {icon}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
          <div
            style={{
              fontFamily: 'var(--sans)',
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-1)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
          {time != null && (
            <div
              className="tnum"
              style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', flexShrink: 0, marginTop: 1 }}
            >
              {time}
            </div>
          )}
        </div>
        {sub != null && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-2)',
              marginBottom: chips ? 5 : 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </div>
        )}
        {chips != null && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{chips}</div>}
        {selected && expansion != null && (
          <div style={{ marginTop: 14, paddingTop: 16, borderTop: '1px solid var(--border)' }}>{expansion}</div>
        )}
      </div>
      {trailing != null && <div style={{ flexShrink: 0, alignSelf: 'flex-start', marginTop: 1 }}>{trailing}</div>}
    </div>
  )
}
